import { ThirdPartyPayment, PaymentGateway } from 'constants/tripla'
import i18n from '../../i18n'
import cctype from 'credit-card-type'
import { checkIfUnderGMOMaintenance } from 'utilities/creditCardMaintenance'

export const MAX_NUMBER_OF_REGISTERED_CREDIT_CARDS = 4

// We do not support new card registration for TapPay at all
export const HARDCODED_DISABLED_CAN_REGISTER_NEW_CARD_PAYMENT_GATEWAYS = [ThirdPartyPayment.TapPay]

export const PAYPAL_SUPPORT_BRANDS = ['visa', 'mastercard']
export const PONTA_PAYPAL_SUPPORT_BRANDS = ['ponta_visa']

export const TapPayConfig = {
  fields: {
    number: {
      element: '#tp-card-number',
      placeholder: i18n.t('creditCardForm.cardNumberHelp')
    },
    expirationDate: {
      // DOM object
      element: '#tp-card-expiration-date',
      placeholder: 'MM / YY'
    },
    ccv: {
      element: '#tp-card-ccv',
      placeholder: i18n.t('creditCardForm.cardSecurityCode')
    }
  },
  styles: {
    input: {
      'font-size': '16px',
      color: '#495057'
    }
  }
}

export function loadScript({ type, url, clientToken }, callback) {
  const paymentScript = document.createElement('script')
  if (callback) paymentScript.onload = callback
  if (type === ThirdPartyPayment.GMO && !window.Multipayment) {
    paymentScript.setAttribute('src', url)
    document.head.appendChild(paymentScript)
  } else if (type === ThirdPartyPayment.GMOPayPal && !window.Multipayment) {
    paymentScript.setAttribute('src', url)
    document.head.appendChild(paymentScript)
  } else if (type === ThirdPartyPayment.AdyenGMO && !window.Multipayment) {
    paymentScript.setAttribute('src', url)
    document.head.appendChild(paymentScript)
  } else if (type === ThirdPartyPayment.AdyenGMOPayPal && !window.Multipayment) {
    paymentScript.setAttribute('src', url)
    document.head.appendChild(paymentScript)
  } else if (
    [ThirdPartyPayment.Paygent, ThirdPartyPayment.PaygentPayPal].includes(type) &&
    !window.PaygentToken
  ) {
    paymentScript.setAttribute('src', url)
    document.head.appendChild(paymentScript)
  } else if (type === ThirdPartyPayment.TapPay && !window.TPDirect) {
    paymentScript.setAttribute('src', url)
    document.head.appendChild(paymentScript)
  } else if (type === ThirdPartyPayment.PayPal) {
    paymentScript.setAttribute('src', url)
    paymentScript.setAttribute('data-client-token', clientToken)
    document.head.appendChild(paymentScript)
  }
}

export function registerCard(
  { shopId, token, type, holdername, cardno, expireMonth, expireYear, cvv },
  callback
) {
  let cardToken
  let resultDetails
  if (
    type === ThirdPartyPayment.GMO ||
    type === ThirdPartyPayment.GMOPayPal ||
    type === ThirdPartyPayment.AdyenGMO ||
    type === ThirdPartyPayment.AdyenGMOPayPal
  ) {
    if (window.Multipayment) {
      window.Multipayment.init(shopId)
      window.Multipayment.getToken(
        {
          holdername,
          cardno,
          expire: expireYear.toString().slice(2, 4) + expireMonth,
          securitycode: cvv
        },
        (response) => {
          const code = response.resultCode
          resultDetails = gmoStatusCodeToReason[code] ?? 'unknown'
          cardToken = response?.tokenObject?.token

          // eslint-disable-next-line standard/no-callback-literal
          callback({
            cardToken,
            resultDetails,
            response
          })
        }
      )
    }
  } else if ([ThirdPartyPayment.Paygent, ThirdPartyPayment.PaygentPayPal].includes(type)) {
    const paygentToken = new window.PaygentToken()
    paygentToken.createToken(
      shopId,
      token,
      {
        card_number: cardno,
        expire_year: expireYear.toString().slice(2, 4),
        expire_month: expireMonth.toString().padStart(2, '0'),
        cvc: cvv,
        name: holdername
      },
      (response) => {
        const code = response.result
        resultDetails = paygentStatusCodeToReason[code] ?? 'unknown'
        cardToken = response?.tokenizedCardObject?.token

        if (!cardToken && resultDetails === 'success') {
          resultDetails = 'unknown'
        }

        // eslint-disable-next-line standard/no-callback-literal
        callback({
          cardToken,
          resultDetails,
          response
        })
      }
    )
  } else if (type === ThirdPartyPayment.TapPay) {
    window.TPDirect.card.getPrime((response) => {
      if (response.status !== 0) {
        // eslint-disable-next-line standard/no-callback-literal
        callback({
          formError: response.msg,
          response
        })
        return
      }
      // eslint-disable-next-line standard/no-callback-literal
      callback({
        cardToken: response.card.prime,
        resultDetails: 'success',
        response
      })
    })
  }
}

// https://www.rubydoc.info/gems/gmo/GMO/Const
// https://hapicode.com/doc/gmo-token.html#%E5%87%A6%E7%90%86%E7%B5%90%E6%9E%9C%E3%82%B3%E3%83%BC%E3%83%89%E4%B8%80%E8%A6%A7
const gmoStatusCodeToReason = {
  '000': 'success',
  100: 'card',
  101: 'card',
  102: 'card',
  109: 'card',
  110: 'date',
  111: 'date',
  112: 'date',
  113: 'date',
  119: 'date',
  121: 'cvv',
  122: 'cvv',
  129: 'cvv',
  131: 'name_format',
  132: 'name',
  139: 'name'
}

// https://umami-me.atlassian.net/wiki/spaces/TECH/pages/1202159728/Get+paygent+card+token
const paygentStatusCodeToReason = {
  '0000': 'success',
  1300: 'card',
  1301: 'card',
  1400: 'date',
  1401: 'date',
  1500: 'date',
  1501: 'date',
  1502: 'date',
  1600: 'cvv',
  1601: 'cvv',
  1700: 'name'
}

const PAYPAL_FE_HANDLED_ISSUE = [
  'INVALID_PARAMETER_SYNTAX',
  'INVALID_EXPIRATION_YEAR',
  'Invalid card number'
]

const PAYPAL_FILED_NAME = {
  '/payment_source/card/security_code': 'ccv',
  '/payment_source/card/expiry': 'expiry',
  '/payment_source/card/number': 'number'
}

const PAYPAL_ERROR_NAMES = ['INVALID_REQUEST', 'VALIDATION_ERROR', 'UNPROCESSABLE_ENTITY']

export const getPaypalIssue = (res) => {
  if (PAYPAL_ERROR_NAMES.includes(res?.name)) {
    return res.details?.[0]?.issue
  }
}

export const getPaypalIssueMessage = (err, vm) => {
  const issue = getPaypalIssue(err)
  if (PAYPAL_FE_HANDLED_ISSUE.includes(issue)) {
    return vm.$t(`triplaPayIssues.${issue.replace(/[-\s]+/gi, '_')}`)
  }
}

export const getPaypalErrorField = (res) => {
  if (PAYPAL_ERROR_NAMES.includes(res?.name)) {
    return PAYPAL_FILED_NAME[res.details?.[0]?.field] || ''
  }
}

export const getPaypalError = (res) => {
  if (PAYPAL_ERROR_NAMES.includes(res?.name)) {
    const detail = res.details?.[0]
    const detailCode = `${PAYPAL_FILED_NAME[detail?.field] || ''}${detail?.issue || ''}`
    return detailCode || res.message
  }
}

export const isPaypalFieldsOk = (evt) => {
  return (
    evt?.fields?.number?.isEmpty === false &&
    evt?.fields?.expirationDate?.isEmpty === false &&
    evt?.fields?.cvv?.isEmpty === false
  )
}

export const prepareCreditCards = (cards = []) => {
  return cards?.map((card) => ({
    ...card,
    card_brand: (card?.card_brand || cctype(card?.number)?.[0]?.type || '')
      .replace('-', '')
      .toLowerCase()
  }))
}

export const isCardOKForGMOPaypal = (card) => {
  const isUnderGMOMaintenance = checkIfUnderGMOMaintenance()

  return card?.gateway === 'paypal' || (card?.gateway === 'gmo' && !isUnderGMOMaintenance)
}

export const isCardOKForPaygentPaypal = (card) => {
  return (
    card?.gateway === 'paypal' ||
    // (card?.gateway === 'paygent' && !['2', '4', '5'].includes(card?.number?.[0]))
    (card?.gateway === 'paygent' && !['visa', 'master'].includes(card?.card_brand))
  )
}

export const isCardOKForGMO = (card) => {
  const isUnderGMOMaintenance = checkIfUnderGMOMaintenance()

  return card.gateway === 'gmo' && !isUnderGMOMaintenance
}

export const isCardOKForPaygent = (card) => {
  return card.gateway === ThirdPartyPayment.Paygent
}

export const isCardOKForAdyenGMO = (card = {}) => {
  const { gateway, card_brand } = card
  const isUnderGMOMaintenance = checkIfUnderGMOMaintenance()

  switch (gateway) {
    case PaymentGateway.GMO:
      return (
        (isVisaCard(card_brand) ||
          isMasterCard(card_brand) ||
          isAmexCard(card_brand) ||
          isJCBCard(card_brand) ||
          isDinersCard(card_brand)) &&
        !isUnderGMOMaintenance
      )
    case PaymentGateway.Adyen:
      return isVisaCard(card_brand) || isMasterCard(card_brand)
    default:
      return false
  }
}

export const isCardOKForAdyenGMOPaypal = (card = {}, isPontaVisaSupport = false) => {
  const { gateway, card_brand } = card
  const isUnderGMOMaintenance = checkIfUnderGMOMaintenance()

  switch (gateway) {
    case PaymentGateway.GMO:
      return isAmexCard(card_brand) && !isUnderGMOMaintenance
    case PaymentGateway.PayPal:
      return isPontaVisaSupport ? false : isVisaCard(card_brand) || isMasterCard(card_brand)
    case PaymentGateway.Adyen:
      return isPontaVisaSupport
        ? isVisaCard(card_brand) ||
            isMasterCard(card_brand) ||
            isJCBCard(card_brand) ||
            isDinersCard(card_brand)
        : isJCBCard(card_brand) || isDinersCard(card_brand)
    default:
      return false
  }
}

export const isAmexCard = (cardBrand) =>
  ['amex', 'american-express', 'americanexpress'].includes(cardBrand)
export const isVisaCard = (cardBrand) => ['visa'].includes(cardBrand)
export const isMasterCard = (cardBrand) => ['master', 'mastercard', 'mc'].includes(cardBrand)
export const isJCBCard = (cardBrand) => ['jcb'].includes(cardBrand)
export const isDinersCard = (cardBrand) => ['diners', 'dinersclub'].includes(cardBrand)

export const cardBrandChecker = {
  master: isMasterCard,
  mastercard: isMasterCard,
  visa: isVisaCard,
  jcb: isJCBCard,
  diners: isDinersCard,
  dinersclub: isDinersCard,
  amex: isAmexCard,
  americanexpress: isAmexCard,
  'american-express': isAmexCard
}
